import type React from "react";
import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import "./TrustSection.css";

const NOISE_GLSL = `
  vec3 mod289(vec3 x)
  {
    return x - floor(x * (1.0 / 289.0)) * 289.0;
  }

  vec4 mod289(vec4 x)
  {
    return x - floor(x * (1.0 / 289.0)) * 289.0;
  }

  vec4 permute(vec4 x)
  {
    return mod289(((x*34.0)+1.0)*x);
  }

  vec4 taylorInvSqrt(vec4 r)
  {
    return 1.79284291400159 - 0.85373472095314 * r;
  }

  vec3 fade(vec3 t) {
    return t*t*t*(t*(t*6.0-15.0)+10.0);
  }

  // Classic Perlin noise, periodic variant
  float pnoise(vec3 P, vec3 rep)
  {
    vec3 Pi0 = mod(floor(P), rep);
    vec3 Pi1 = mod(Pi0 + vec3(1.0), rep);
    Pi0 = mod289(Pi0);
    Pi1 = mod289(Pi1);
    vec3 Pf0 = fract(P);
    vec3 Pf1 = Pf0 - vec3(1.0);
    vec4 ix = vec4(Pi0.x, Pi1.x, Pi0.x, Pi1.x);
    vec4 iy = vec4(Pi0.yy, Pi1.yy);
    vec4 iz0 = Pi0.zzzz;
    vec4 iz1 = Pi1.zzzz;

    vec4 ixy = permute(permute(ix) + iy);
    vec4 ixy0 = permute(ixy + iz0);
    vec4 ixy1 = permute(ixy + iz1);

    vec4 gx0 = ixy0 * (1.0 / 7.0);
    vec4 gy0 = fract(floor(gx0) * (1.0 / 7.0)) - 0.5;
    gx0 = fract(gx0);
    vec4 gz0 = vec4(0.5) - abs(gx0) - abs(gy0);
    vec4 sz0 = step(gz0, vec4(0.0));
    gx0 -= sz0 * (step(0.0, gx0) - 0.5);
    gy0 -= sz0 * (step(0.0, gy0) - 0.5);

    vec4 gx1 = ixy1 * (1.0 / 7.0);
    vec4 gy1 = fract(floor(gx1) * (1.0 / 7.0)) - 0.5;
    gx1 = fract(gx1);
    vec4 gz1 = vec4(0.5) - abs(gx1) - abs(gy1);
    vec4 sz1 = step(gz1, vec4(0.0));
    gx1 -= sz1 * (step(0.0, gx1) - 0.5);
    gy1 -= sz1 * (step(0.0, gy1) - 0.5);

    vec3 g000 = vec3(gx0.x,gy0.x,gz0.x);
    vec3 g100 = vec3(gx0.y,gy0.y,gz0.y);
    vec3 g001 = vec3(gx1.x,gy1.x,gz1.x);
    vec3 g101 = vec3(gx1.y,gy1.y,gz1.y);
    vec3 g010 = vec3(gx0.z,gy0.z,gz0.z);
    vec3 g110 = vec3(gx0.w,gy0.w,gz0.w);
    vec3 g011 = vec3(gx1.z,gy1.z,gz1.z);
    vec3 g111 = vec3(gx1.w,gy1.w,gz1.w);

    vec4 norm0 = taylorInvSqrt(vec4(dot(g000, g000), dot(g010, g010), dot(g100, g100), dot(g110, g110)));
    g000 *= norm0.x;
    g010 *= norm0.y;
    g100 *= norm0.z;
    g110 *= norm0.w;
    vec4 norm1 = taylorInvSqrt(vec4(dot(g001, g001), dot(g011, g011), dot(g101, g101), dot(g111, g111)));
    g001 *= norm1.x;
    g011 *= norm1.y;
    g101 *= norm1.z;
    g111 *= norm1.w;

    float n000 = dot(g000, Pf0);
    float n100 = dot(g100, vec3(Pf1.x, Pf0.yz));
    float n010 = dot(g010, vec3(Pf0.x, Pf1.y, Pf0.z));
    float n110 = dot(g110, vec3(Pf1.xy, Pf0.z));
    float n001 = dot(g001, vec3(Pf0.xy, Pf1.z));
    float n101 = dot(g101, vec3(Pf1.x, Pf0.y, Pf1.z));
    float n011 = dot(g011, vec3(Pf0.x, Pf1.yz));
    float n111 = dot(g111, Pf1);

    vec3 fade_xyz = fade(Pf0);
    vec4 n_z = mix(vec4(n000, n100, n010, n110), vec4(n001, n101, n011, n111), fade_xyz.z);
    vec2 n_yz = mix(n_z.xy, n_z.zw, fade_xyz.y);
    float n_xyz = mix(n_yz.x, n_yz.y, fade_xyz.x);
    return 2.2 * n_xyz;
  }
`;

const ROTATION_GLSL = `
  mat3 rotation3dY(float angle) {
    float s = sin(angle);
    float c = cos(angle);

    return mat3(
      c, 0.0, -s,
      0.0, 1.0, 0.0,
      s, 0.0, c
    );
  }
  
  vec3 rotateY(vec3 v, float angle) {
    return rotation3dY(angle) * v;
  }  
`;

const VERTEX_SHADER = `
  varying vec2 vUv;
  varying float vDistort;
  
  uniform float uTime;
  uniform float uSpeed;
  uniform float uNoiseDensity;
  uniform float uNoiseStrength;
  uniform float uFrequency;
  uniform float uAmplitude;
  
  ${NOISE_GLSL}
  ${ROTATION_GLSL}
  
  void main() {
    vUv = uv;
    
    float t = uTime * uSpeed;
    float distortion = pnoise((normal + vec3(t)) * uNoiseDensity, vec3(10.0)) * uNoiseStrength;

    vec3 pos = position + (normal * distortion);
    float angle = sin(uv.y * uFrequency + t) * uAmplitude;
    pos = rotateY(pos, angle);    
    
    vDistort = distortion;

    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.);
  }
`;

const FRAGMENT_SHADER = `
  varying vec2 vUv;
  varying float vDistort;
  
  uniform float uTime;
  uniform float uIntensity;
  
  vec3 cosPalette(float t, vec3 a, vec3 b, vec3 c, vec3 d) {
    return a + b * cos(6.28318 * (c * t + d));
  }     
  
  void main() {
    float distort = vDistort * uIntensity;
    
    vec3 brightness = vec3(0.5, 0.5, 0.5);
    vec3 contrast = vec3(0.5, 0.5, 0.5);
    vec3 oscilation = vec3(1.0, 1.0, 1.0);
    vec3 phase = vec3(0.0, 0.1, 0.2);
  
    vec3 color = cosPalette(distort, brightness, contrast, oscilation, phase);
    
    gl_FragColor = vec4(color, 1.0);
  }
`;

const SETTINGS = {
  speed: 0.2,
  density: 1.5,
  strength: 0.2,
  frequency: 3.0,
  amplitude: 6.0,
  intensity: 7.0,
};

const DistortedSphere: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const width = 249;
    const height = 249;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
    camera.position.set(0, 0, 3.8);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: "high-performance" });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(width, height, false);
    renderer.setClearColor(0x000000, 0);

    const canvas = renderer.domElement;
    canvas.className = "sphere-3d-canvas";
    container.appendChild(canvas);

    const controls = new OrbitControls(camera, canvas);
    controls.enableZoom = false;
    controls.enablePan = false;
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.autoRotate = true;
    controls.autoRotateSpeed = 1.0;

    const geometry = new THREE.IcosahedronGeometry(1.05, 64);
    const material = new THREE.ShaderMaterial({
      vertexShader: VERTEX_SHADER,
      fragmentShader: FRAGMENT_SHADER,
      uniforms: {
        uTime: { value: 0 },
        uSpeed: { value: SETTINGS.speed },
        uNoiseDensity: { value: SETTINGS.density },
        uNoiseStrength: { value: SETTINGS.strength },
        uFrequency: { value: SETTINGS.frequency },
        uAmplitude: { value: SETTINGS.amplitude },
        uIntensity: { value: SETTINGS.intensity },
      },
    });

    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);

    const clock = new THREE.Clock();
    let animId: number;

    const animate = () => {
      animId = requestAnimationFrame(animate);
      controls.update();

      const elapsed = clock.getElapsedTime();
      material.uniforms.uTime.value = elapsed;

      renderer.render(scene, camera);
    };

    animate();

    return () => {
      cancelAnimationFrame(animId);
      controls.dispose();
      geometry.dispose();
      material.dispose();
      renderer.dispose();
      if (canvas.parentElement) {
        canvas.parentElement.removeChild(canvas);
      }
    };
  }, []);

  return <div ref={containerRef} className="sphere-3d-mount" />;
};

export const TrustSection: React.FC = () => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [canvasScale, setCanvasScale] = useState<number>(1);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  useEffect(() => {
    let rafId: number;

    const updateScale = () => {
      const isNarrow = window.matchMedia("(max-width: 699.98px)").matches;
      setIsMobile(isNarrow);

      if (isNarrow) {
        setCanvasScale(1);
        return;
      }

      if (wrapperRef.current) {
        const width = wrapperRef.current.clientWidth;
        if (width > 0) {
          const scale = width / 1690;
          setCanvasScale(scale);
        }
      }
    };

    const debouncedUpdate = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateScale);
    };

    updateScale();
    window.addEventListener("resize", debouncedUpdate, { passive: true });

    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(() => {
        debouncedUpdate();
      });
      if (wrapperRef.current?.parentElement) {
        observer.observe(wrapperRef.current.parentElement);
      }
    }

    const errorHandler = (e: ErrorEvent) => {
      if (
        e.message?.includes("ResizeObserver loop") ||
        e.message?.includes("ResizeObserver loop completed with undelivered notifications") ||
        e.message?.includes("ResizeObserver loop limit exceeded")
      ) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    };
    window.addEventListener("error", errorHandler);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", debouncedUpdate);
      window.removeEventListener("error", errorHandler);
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  const wrapperStyle: React.CSSProperties = {
    ...({ "--canvas-scale": canvasScale } as React.CSSProperties),
    height: isMobile ? "auto" : `${950 * canvasScale}px`,
  };

  return (
    <div className="cynaps-trust-outer">
      <div className="cynaps-trust-frame">
        {/* Whitish-Greyish Border Framed Section */}
        <section
          ref={wrapperRef}
          className="vela-section-wrapper"
          aria-label="Validation Infrastructure Overview"
          style={wrapperStyle}
        >
          <div className="reference-canvas">
            <main className="shell" aria-labelledby="page-title">
              {/* Distinctive Technical Header */}
              <section className="intro">
                <h1 id="page-title">
                  <span className="headline-sans">Validation infrastructure that</span>
                  <span className="headline-serif">proves frontier AI.</span>
                </h1>
                <p>
                  The independent evaluation platform for teams that can’t compromise — stream directly from
                  your private VPC, enforce mathematical consensus, and deploy with audit-proof confidence.
                </p>
              </section>

              {/* Cards Section with Enhanced Core Animations & Real Substance */}
              <section className="cards" aria-label="Validation infrastructure pillars">
                {/* ==============================================================
                    CARD 1: PLANETARY HORIZON LIMB (Zero-Egress Ephemeral Stream)
                    ============================================================== */}
                <article className="card card-left" tabIndex={0} aria-label="Zero-egress streaming architecture">
                  {/* Core Surface Animation: Vertical Swell with Luminous Atmospheric Corona & Precision Horizon Arc */}
                  <div className="left-surface" aria-hidden="true">
                    <div className="horizon-corona" />
                    <div className="horizon-arc" />
                  </div>
                  {/* Steady, High-Signal Glass Pills */}
                  <span className="glass-pill region-left">AWS us-east-1</span>
                  <span className="glass-pill region-right">GCP europe-west2</span>
                  <span className="glass-pill live">
                    <i className="dot" aria-hidden="true" />
                    <span className="live-copy">Zero-Egress Stream</span>
                  </span>
                  <span className="glass-pill regions">Private VPC · 0 Retained</span>
                  
                  <h2 className="card-title">
                    Zero raw data egress for <strong>planetary-scale models.</strong>
                  </h2>
                </article>

                {/* ==============================================================
                    CARD 2: ORBIT SPHERE (Multi-Expert Statistical Consensus)
                    ============================================================== */}
                <article
                  className="card card-middle"
                  tabIndex={0}
                  aria-label="Multi-expert statistical consensus protocol"
                >
                  {/* Core Orbit Animation: 379px Rotating Dashed Ring + Concentric Resonant Orbital Trace */}
                  <div className="orbit" aria-hidden="true">
                    <div className="orbit-inner" />
                    <div className="orbit-dot-bottom" />
                  </div>
                  
                  {/* Procedural 3D WebGL Distorted Sphere */}
                  <div className="sphere" aria-hidden="true">
                    <DistortedSphere />
                  </div>
                  
                  {/* Calibrated Evaluation Telemetry (Steady, Non-Blinking, Real Metrics) */}
                  <span className="glass-pill latency">
                    <i className="dot" aria-hidden="true" />
                    <span className="telemetry-number">0.88</span>&nbsp; κ · Fleiss
                  </span>
                  <span className="glass-pill error">
                    <i className="dot" aria-hidden="true" />
                    <span className="telemetry-number">0.00</span>&nbsp; % data egress
                  </span>
                  <span className="glass-pill requests">
                    <i className="dot violet" aria-hidden="true" />
                    <span className="telemetry-number">99.4</span>&nbsp; % consensus
                  </span>
                  
                  <p className="card-copy">Multi-expert consensus scoring across every modality you evaluate.</p>
                </article>

                {/* ==============================================================
                    CARD 3: EXPANDING STATUS BAND (Adaptive Calibration & Audit)
                    ============================================================== */}
                <article className="card card-right" tabIndex={0} aria-label="Adaptive calibration and audit trials">
                  {/* Core Expanding Status Band Animation with Multi-Spectral Bloom & Luminous Boundary Beam */}
                  <div className="status-band" aria-hidden="true" />
                  <div className="status-band-beam" aria-hidden="true" />
                  
                  {/* Status Stack with Steady Glowing Indicators */}
                  <div className="status-stack" aria-hidden="true">
                    <div className="status">
                      <i className="dot yellow" />
                      <span>Triple-blind cohort</span>
                      <em>calibrated</em>
                    </div>
                    <div className="status">
                      <i className="dot" />
                      <span>Cryptographic audit gate</span>
                      <em>verified</em>
                    </div>
                  </div>
                  
                  <h2 className="card-title">Adaptive.</h2>
                </article>
              </section>
            </main>
          </div>
        </section>
      </div>
    </div>
  );
};
